import { DateTime } from "luxon";
import { useCallback } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { getWeekStart } from "../utils/dateUtils";
import { checkIsDraftMode, draftModeAfterCurrentWeek } from "../utils/demoDraftWeeks";
import { DEFAULT_WEEK_START_DAY } from "../utils/models";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";
import { useShownWeek } from "./useShownWeek";

// The hospital and SNF demos treat the weeks from three after the current one as drafts. With the schedule cache off
// that's a position in the old list (`checkIsDraftMode`); on there's no old list, so it's the same rule by date.

/** Whether `date` falls in the demos' draft weeks, by date: from the Sunday three weeks after the current week's. */
const isDemoDraftDate = (date: DateTime, timezone: string): boolean =>
	date.startOf("day") >= getWeekStart(DateTime.now().setZone(timezone), DEFAULT_WEEK_START_DAY).plus({ weeks: draftModeAfterCurrentWeek });

/** Whether a shift on `date` is in the demos' draft weeks; with the cache off, `oldListWeekIndex` decides, as before. */
export const useIsDemoDraftDate = (): ((date: DateTime, oldListWeekIndex: number | undefined) => boolean) => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { timezone } = useHospitalData();
	return useCallback(
		(date: DateTime, oldListWeekIndex: number | undefined) =>
			isCacheEnabled ? isDemoDraftDate(date, timezone) : oldListWeekIndex === undefined || checkIsDraftMode(oldListWeekIndex),
		[isCacheEnabled, timezone],
	);
};

/** Whether the shown week is one of the demos' draft weeks; with the cache off, the old list's `weekIndex` decides. */
export const useIsDemoDraftWeek = (weekIndex: number): boolean => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { timezone } = useHospitalData();
	const { weekStart } = useShownWeek();
	return isCacheEnabled ? weekStart !== undefined && isDemoDraftDate(weekStart, timezone) : checkIsDraftMode(weekIndex);
};
